/** @jsxImportSource @emotion/react */
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { memo } from 'react'
import Text from '../ts-components/Text'
import { css } from '@emotion/react'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'

dayjs.extend(utc)

interface ScheduleRegistration {
  id: string
  schedule_id: string
  is_team_registration: boolean
  is_player_registration: boolean
  payment_provider_type: {
    id: string
    name: string
  }
  show_waiver: boolean
  is_live: boolean
  starts_at: string
  ends_at: string
  custom_link: string
  dark_theme: boolean
}

interface Props {
  registration: ScheduleRegistration | null
  isRegistrationOn: boolean
}

const tagStyles = css`
  min-width: 100px;
  text-align: center;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 6px 8px;
  border-radius: 8px;
  gap: 8px;
`

/**
 * RegistrationStatusTag displays the current status of a schedule's registration
 * based on various conditions including live status, start/end dates.
 *
 * Rules:
 * - If is_live is true: "Open" (green) regardless of dates
 * - If is_live is false:
 *   - If current date is before starts_at: "Open Soon" (blue)
 *   - If current date is between starts_at and ends_at: "Open" (green)
 *   - If current date is after ends_at: "Closed" (red)
 * - If isRegistrationOn is false or registration is null: nothing is displayed
 *
 * Each status includes:
 * - Appropriate icon (CheckCircle for Open, ClockCircle for Open Soon, CloseCircle for Closed)
 * - Color-coded background and border
 * - Tooltip with start/end dates, live status, and registration link if available
 *
 * @param {Props} props - Component props
 * @returns {JSX.Element | null} The registration status tag or null if no status should be shown
 */
const RegistrationStatusTag = ({ registration, isRegistrationOn }: Props) => {
  if (!isRegistrationOn) {
    return null
  }
  if (!registration) {
    const tooltipContent = (
      <div>
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          Registration Is Not Set Yet.
        </Text>
      </div>
    )

    const backgroundColor = colors.NEUTRAL_100
    const borderColor = colors.NEUTRAL_200
    const icon = <InfoCircleOutlined />
    const status = 'Not Set'

    return (
      <Tooltip title={tooltipContent} placement='right'>
        <div
          css={[
            tagStyles,
            css`
              background-color: ${backgroundColor};
              border: 1px solid ${borderColor};
            `,
          ]}
        >
          {icon}
          <Text
            variant='paragraphXSmall'
            weight='medium'
            color={colors.NEUTRAL_0}
          >
            {status}
          </Text>
        </div>
      </Tooltip>
    )
  }

  const now = dayjs.utc()
  const startsAt = dayjs.utc(registration.starts_at)
  const endsAt = dayjs.utc(registration.ends_at)

  let status: string | null = null
  let tagColor: string | null = null
  let backgroundColor: string | null = null
  let borderColor: string | null = null
  let icon: React.ReactNode | null = null

  // If the registration is live, we need to check if the current date is before the end date
  if (registration.is_live) {
    status = 'Open'
    tagColor = colors.SUCCESS_300
    backgroundColor = 'rgba(57, 187, 85, 0.20);'
    borderColor = colors.SUCCESS_200
    icon = <CheckCircleOutlined />
  }
  if (!registration.is_live) {
    if (now.isBefore(endsAt) || now.isSame(endsAt)) {
      status = 'Open'
      tagColor = colors.SUCCESS_300
      backgroundColor = 'rgba(57, 187, 85, 0.20);'
      borderColor = colors.SUCCESS_200
      icon = <CheckCircleOutlined />
    } else {
      status = 'Closed'
      tagColor = colors.ERROR_300
      backgroundColor = ' rgba(223, 66, 66, 0.20);'
      borderColor = colors.ERROR_200
      icon = <CloseCircleOutlined />
    }
    if (now.isAfter(startsAt) && now.isBefore(endsAt)) {
      status = 'Open'
      tagColor = colors.SUCCESS_300
      backgroundColor = 'rgba(57, 187, 85, 0.20);'
      borderColor = colors.SUCCESS_200
      icon = <CheckCircleOutlined />
    } else if (now.isBefore(startsAt)) {
      status = 'Open Soon'
      tagColor = colors.INFO_300
      backgroundColor = 'rgba(90, 166, 255, 0.20);'
      borderColor = colors.INFO_200
      icon = <ClockCircleOutlined />
    }
  }

  if (!status || !tagColor) {
    return null
  }

  const tooltipContent = (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 4px;
      `}
    >
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        Start Date: {startsAt.format("MMM DD, 'YY")}
      </Text>
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        End Date: {endsAt.format("MMM DD, 'YY")}
      </Text>
      {registration.is_live && (
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          Registration is currently live
        </Text>
      )}
      {registration.custom_link && (
        <a
          href={registration.custom_link}
          target='_blank'
          rel='noopener noreferrer'
          css={css`
            color: ${colors.WHITE};
            text-decoration: underline;
            &:hover {
              color: ${colors.WHITE};
              opacity: 0.8;
            }
          `}
        >
          Registration Link
        </a>
      )}
    </div>
  )

  return (
    <Tooltip title={tooltipContent} placement='right'>
      <div
        css={[
          tagStyles,
          css`
            background-color: ${backgroundColor};
            border: 1px solid ${borderColor};
          `,
        ]}
      >
        {icon}
        <Text
          variant='paragraphXSmall'
          weight='medium'
          color={colors.NEUTRAL_0}
        >
          {status}
        </Text>
      </div>
    </Tooltip>
  )
}

export default memo(RegistrationStatusTag)
